import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Check, Zap, Star, Crown, Building, Loader2 } from "lucide-react";
import { Header } from "@/components/layout/header";
import { useAuth } from "@/hooks/use-auth";
import { useLocation } from "wouter";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { SubscriptionProduct, UserSubscription } from "../../../shared/schema";

// Extend SubscriptionProduct with calculated fields for display
interface SubscriptionPlanDisplay extends SubscriptionProduct {
  monthlyPricePerPage: number;
  annualPricePerPage: number;
  annualDiscountPercent: number;
  pagesPerMonth: number;
}

// Backend response format for /api/user/subscription
interface UserSubscriptionFormatted {
  id: string;
  status: string;
  planName: string;
  pointsPerMonth: number;
  currentPeriodEnd: string;
}

// Initialize Stripe
if (!import.meta.env.VITE_STRIPE_PUBLIC_KEY) {
  throw new Error('Missing required Stripe key: VITE_STRIPE_PUBLIC_KEY');
}
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY);

const formatPrice = (cents: number) => {
  return (cents / 100).toFixed(2);
};

function getSubscriptionProductByPriceId(priceId: string | undefined, subscriptionList: SubscriptionProduct[]): SubscriptionProduct | null {
    if(!priceId) return null;
    for (const sub of subscriptionList) {
        if (sub.monthlyPriceId === priceId || sub.annualPriceId === priceId) return sub;
    }
    return null; // Not found
}

// Subscription Checkout Form Component
const SubscriptionCheckoutForm = ({ priceId, planName, onSuccess, onCancel }: {
  priceId: string;
  planName: string;
  onSuccess: () => void;
  onCancel: () => void;
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const { toast } = useToast();
  const [isProcessing, setIsProcessing] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);

    try {
      const { error } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: window.location.origin + '/pricing',
        },
      });

      if (error) {
        toast({
          title: "Payment Failed",
          description: error.message,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Subscription Created",
          description: `You are now subscribed to the ${planName} plan!`,
        });
        onSuccess();
      }
    } catch (err) {
      toast({
        title: "Error",
        description: "An unexpected error occurred",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <PaymentElement />
      <div className="flex gap-4">
        <Button 
          type="submit" 
          disabled={!stripe || isProcessing}
          className="flex-1"
        >
          {isProcessing ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Processing...
            </>
          ) : (
            `Subscribe to ${planName}`
          )}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
};

const getPlanIcon = (name: string) => {
  switch (name.toLowerCase()) {
    case 'starter': return <Zap className="h-6 w-6" />;
    case 'pro': return <Star className="h-6 w-6" />;
    case 'studio': return <Crown className="h-6 w-6" />;
    case 'agency': return <Building className="h-6 w-6" />;
    default: return <Zap className="h-6 w-6" />;
  }
};

const getPlanColor = (name: string) => {
  switch (name.toLowerCase()) {
    case 'starter': return 'from-blue-500 to-cyan-500';
    case 'pro': return 'from-purple-500 to-pink-500';
    case 'studio': return 'from-orange-500 to-red-500';
    case 'agency': return 'from-gray-800 to-black';
    default: return 'from-blue-500 to-cyan-500';
  }
};

const getRetailPrice = () => {
  // Retail price is $0.03 per point, so $3.00 per page (100 points)
  return 300; // cents per page
};

// Helper function to calculate price difference between plans
const calculatePriceDifference = (currentPlanName: string, newPlanName: string, newBillingCycle: 'monthly' | 'annual', subscriptions: SubscriptionProduct[], userSubscriptionRaw?: UserSubscription | null) => {
  const currentPlan = subscriptions.find(plan => plan.name.toLowerCase() === currentPlanName.toLowerCase());
  const newPlan = subscriptions.find(plan => plan.name.toLowerCase() === newPlanName.toLowerCase());
  
  if (!currentPlan || !newPlan) return 0;
  
  // Determine current user's billing cycle from priceId
  let currentPrice = 0;
  if (userSubscriptionRaw?.priceId === currentPlan.monthlyPriceId) {
    currentPrice = currentPlan.monthlyPrice;
  } else if (userSubscriptionRaw?.priceId === currentPlan.annualPriceId) {
    currentPrice = currentPlan.annualPrice;
  } else {
    // Fallback to monthly if we can't determine
    currentPrice = currentPlan.monthlyPrice;
  }
  
  const newPrice = newBillingCycle === 'monthly' ? newPlan.monthlyPrice : newPlan.annualPrice;
  
  return newPrice - currentPrice;
};

export default function PricingPage() {
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('monthly');
  const [checkoutData, setCheckoutData] = useState<{ clientSecret: string; priceId: string; planName: string } | null>(null);
  const [confirmationDialog, setConfirmationDialog] = useState<{
    isOpen: boolean;
    type: 'upgrade' | 'downgrade';
    planName: string;
    priceId: string;
    priceDifference?: number;
  }>({
    isOpen: false,
    type: 'upgrade',
    planName: '',
    priceId: '',
    priceDifference: 0
  });
  const [confirmationChecked, setConfirmationChecked] = useState(false);
  const [cancelConfirmationOpen, setCancelConfirmationOpen] = useState(false);
  const [cancelConfirmationChecked, setCancelConfirmationChecked] = useState(false);
  const { user } = useAuth();
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  const { data: subscriptions, isLoading, error } = useQuery<SubscriptionProduct[]>({
    queryKey: ['/api/subscriptions'],
  });

  // Get user's current subscription if logged in (backend returns formatted response)
  const { data: userSubscription } = useQuery<UserSubscriptionFormatted | null>({
    queryKey: ["/api/user/subscription"],
    enabled: !!user
  });

  // Get raw subscription data for priceId detection
  const { data: userSubscriptionRaw } = useQuery<UserSubscription | null>({
    queryKey: ["/api/userSubscription/raw"],
    enabled: !!user && !!userSubscription
  });

  // Transform raw subscription data into display format
  const subscriptionsDisplay: SubscriptionPlanDisplay[] = subscriptions?.map((sub) => ({
    ...sub,
    monthlyPricePerPage: Math.round((sub.monthlyPrice / (sub.pointsPerMonth / 100)) * 100) / 100,
    annualPricePerPage: Math.round((sub.annualPrice / 12 / (sub.pointsPerMonth / 100)) * 100) / 100,
    annualDiscountPercent: Math.round(((sub.monthlyPrice * 12 - sub.annualPrice) / (sub.monthlyPrice * 12)) * 100),
    pagesPerMonth: Math.floor(sub.pointsPerMonth / 100)
  })) || [];

  // Create subscription mutation
  const createSubscriptionMutation = useMutation({
    mutationFn: async ({ priceId, planName }: { priceId: string; planName: string }) => {
      const response = await apiRequest("POST", "/api/userSubscription", { priceId });
      return { ...(await response.json()), planName };
    },
    onSuccess: (data) => {
      setCheckoutData({
        clientSecret: data.clientSecret,
        priceId: data.priceId || '',
        planName: data.planName || 'Selected Plan'
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create subscription",
        variant: "destructive",
      });
    },
  });

  // Update subscription mutation
  const updateSubscriptionMutation = useMutation({
    mutationFn: async (priceId: string) => {
      const response = await apiRequest("PUT", "/api/userSubscription", { priceId });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/user/subscription"] });
      toast({
        title: "Subscription Updated",
        description: "Your subscription has been successfully updated.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update subscription",
        variant: "destructive",
      });
    },
  });

  // Cancel subscription mutation
  const cancelSubscriptionMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("DELETE", "/api/userSubscription");
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/user/subscription"] });
      toast({
        title: "Subscription Cancelled",
        description: "Your subscription will remain active until the end of your billing period.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to cancel subscription",
        variant: "destructive",
      });
    },
  });

  // Handle confirmation dialog proceed
  const handleConfirmationProceed = () => {
    if (!confirmationChecked) return;
    
    updateSubscriptionMutation.mutate(confirmationDialog.priceId);
    setConfirmationDialog({ ...confirmationDialog, isOpen: false });
    setConfirmationChecked(false);
  };

  // Handle confirmation dialog cancel
  const handleConfirmationCancel = () => {
    setConfirmationDialog({ ...confirmationDialog, isOpen: false });
    setConfirmationChecked(false);
  };

  // Handle cancellation confirmation proceed
  const handleCancelConfirmationProceed = () => {
    if (!cancelConfirmationChecked) return;
    
    cancelSubscriptionMutation.mutate();
    setCancelConfirmationOpen(false);
    setCancelConfirmationChecked(false);
  };

  // Handle cancellation confirmation cancel
  const handleCancelConfirmationCancel = () => {
    setCancelConfirmationOpen(false);
    setCancelConfirmationChecked(false);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading subscription plans...</p>
        </div>
      </div>
    );
  }

  if (error || !subscriptions) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
        <Header />
        <div className="flex items-center justify-center min-h-[calc(100vh-64px)]">
          <div className="text-center">
            <p className="text-red-600">Failed to load subscription plans</p>
          </div>
        </div>
      </div>
    );
  }

  const retailPricePerPage = getRetailPrice();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <Header />
      <div className="container mx-auto px-4 py-16">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold text-slate-900 mb-6">
            Choose Your <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Creative Plan</span>
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto mb-8">
            Transform your screenplays into stunning visual content with our AI-powered rendering platform. 
            Choose a subscription for better rates or purchase points at retail price.
          </p>
          
          {/* Billing Toggle */}
          <Tabs value={billingCycle} onValueChange={(value) => setBillingCycle(value as 'monthly' | 'annual')} className="w-fit mx-auto">
            <TabsList className="grid w-full grid-cols-2 bg-white/50 backdrop-blur-sm">
              <TabsTrigger value="monthly">Monthly</TabsTrigger>
              <TabsTrigger value="annual" className="relative">
                Annual
                <Badge variant="secondary" className="ml-2 bg-green-100 text-green-700 text-xs">Save up to 20%</Badge>
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {/* Pricing Cards */}
        <div className="grid lg:grid-cols-4 md:grid-cols-2 gap-8 mb-16">
          {subscriptionsDisplay.map((plan, index) => {
            const currentPrice = billingCycle === 'monthly' ? plan.monthlyPrice : plan.annualPrice;
            const currentPricePerPage = billingCycle === 'monthly' ? plan.monthlyPricePerPage : plan.annualPricePerPage;
            const savingsVsRetail = Math.round(((retailPricePerPage - currentPricePerPage) / retailPricePerPage) * 100);
            const isPopular = plan.name.toLowerCase() === 'pro';
            
            // Check if user has this plan
           // console.log("subscriptions", subscriptions);
            const userPlan = getSubscriptionProductByPriceId(userSubscriptionRaw?.priceId, subscriptions);
            console.log("userSubscriptionRaw?.priceId", userSubscriptionRaw?.priceId, "subscriptions", subscriptions);
            const userPlanName = userPlan?.name || null;
            
            // Get current user's subscription details and determine their billing cycle
            const currentUserPlan = subscriptionsDisplay.find(p => p.name.toLowerCase() === userPlanName?.toLowerCase());
            let currentUserPrice = 0;
            let userBillingCycle: 'monthly' | 'annual' = 'monthly';
            
            if (currentUserPlan && userSubscriptionRaw?.priceId) {
              // Determine billing cycle from priceId
              if (userSubscriptionRaw.priceId === currentUserPlan.monthlyPriceId) {
                userBillingCycle = 'monthly';
                currentUserPrice = currentUserPlan.monthlyPrice;
              } else if (userSubscriptionRaw.priceId === currentUserPlan.annualPriceId) {
                userBillingCycle = 'annual';
                currentUserPrice = currentUserPlan.annualPrice;
              }
            } else if (currentUserPlan) {
              // Fallback: assume monthly if we can't detect billing cycle
              userBillingCycle = 'monthly';
              currentUserPrice = currentUserPlan.monthlyPrice;
            }
            
            // Check if this is the exact same plan (name + billing cycle)
            const hasThisPlan = userPlanName === plan.name && userBillingCycle === billingCycle;
            
            const hasAnySubscription = !!userSubscription;
            //console.log("userPlan", userPlanName, "hasAnySubscription", hasAnySubscription);
            // Determine button text and action
            const getButtonText = () => {
              if (!user) return "Get Started";
              if (hasThisPlan) return "Current Plan";
              if (hasAnySubscription) {
                // If it's the same plan name but different billing cycle, show as upgrade if more expensive
                if (userPlanName?.toLowerCase() === plan.name.toLowerCase() && !hasThisPlan) {
                  return currentPrice > currentUserPrice ? "Upgrade" : "Switch";
                }
                
                // Compare based on price first, then plan levels
                if (currentPrice > currentUserPrice) {
                  return "Upgrade";
                } else if (currentPrice < currentUserPrice) {
                  return "Downgrade";
                } else {
                  // Same price, compare plan levels
                  const planLevels = { starter: 1, pro: 2, studio: 3, agency: 4 };
                  const currentLevel = planLevels[userPlanName?.toLowerCase() as keyof typeof planLevels] || 0;
                  const newLevel = planLevels[plan.name.toLowerCase() as keyof typeof planLevels] || 0;
                  return newLevel > currentLevel ? "Upgrade" : "Switch";
                }
              }
              return "Subscribe";
            };
            
            const handlePlanClick = () => {
              if (!user) {
                navigate("/auth");
                return;
              }
              
              if (hasThisPlan) {
                return; // Current plan, do nothing
              }
              
              // Handle subscription creation or update
              const targetPriceId = billingCycle === 'monthly' ? plan.monthlyPriceId : plan.annualPriceId;
              
              if (hasAnySubscription && userPlanName && subscriptionsDisplay) {
                // Show confirmation dialog for subscription changes
                const priceDifference = calculatePriceDifference(userPlanName, plan.name, billingCycle, subscriptionsDisplay, userSubscriptionRaw);
                
                // Determine if it's an upgrade based on price
                const isUpgrade = currentPrice > currentUserPrice;
                
                setConfirmationDialog({
                  isOpen: true,
                  type: isUpgrade ? 'upgrade' : 'downgrade',
                  planName: plan.name,
                  priceId: targetPriceId,
                  priceDifference: Math.abs(priceDifference)
                });
                setConfirmationChecked(false);
              } else {
                // Create new subscription with Stripe payment
                createSubscriptionMutation.mutate({ priceId: targetPriceId, planName: plan.name });
              }
            };
            
            return (
              <Card key={plan.id} className={`relative overflow-hidden transition-all duration-300 hover:scale-105 ${
                isPopular ? 'ring-2 ring-purple-500 shadow-2xl' : 'hover:shadow-xl'
              }`}>
                {isPopular && (
                  <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-center py-2 text-sm font-semibold">
                    Most Popular
                  </div>
                )}
                
                <CardHeader className={`${isPopular ? 'pt-12' : 'pt-6'} pb-2`}>
                  <div className={`w-12 h-12 rounded-lg bg-gradient-to-r ${getPlanColor(plan.name)} flex items-center justify-center text-white mb-4`}>
                    {getPlanIcon(plan.name)}
                  </div>
                  <CardTitle className="text-2xl font-bold">{plan.name}</CardTitle>
                  <CardDescription className="text-slate-600">
                    {plan.pagesPerMonth} pages per month included
                  </CardDescription>
                </CardHeader>

                <CardContent className="pt-2">
                  <div className="mb-6">
                    <div className="flex items-baseline mb-2">
                      <span className="text-4xl font-bold text-slate-900">
                        ${formatPrice(currentPrice)}
                      </span>
                      <span className="text-slate-600 ml-2">
                        /{billingCycle === 'monthly' ? 'month' : 'year'}
                      </span>
                    </div>
                    
                    {billingCycle === 'annual' && plan.annualDiscountPercent > 0 && (
                      <Badge variant="secondary" className="bg-green-100 text-green-700">
                        {plan.annualDiscountPercent}% off annual
                      </Badge>
                    )}
                  </div>

                  <div className="space-y-3 mb-6">
                    <div className="flex items-center text-sm">
                      <Check className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                      <span className="text-slate-700">
                        ${formatPrice(currentPricePerPage)} per page
                      </span>
                      <Badge variant="outline" className="ml-2 text-xs">
                        {savingsVsRetail}% off retail
                      </Badge>
                    </div>
                    
                    <div className="flex items-center text-sm">
                      <Check className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                      <span className="text-slate-700">
                        {plan.pointsPerMonth.toLocaleString()} points per month
                      </span>
                    </div>
                    
                    <div className="flex items-center text-sm">
                      <Check className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                      <span className="text-slate-700">Priority rendering</span>
                    </div>
                    
                    <div className="flex items-center text-sm">
                      <Check className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                      <span className="text-slate-700">Advanced AI styles</span>
                    </div>
                    
                    {(plan.name.toLowerCase() === 'studio' || plan.name.toLowerCase() === 'agency') && (
                      <div className="flex items-center text-sm">
                        <Check className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                        <span className="text-slate-700">Priority support</span>
                      </div>
                    )}
                  </div>
                </CardContent>

                <CardFooter>
                  <Button 
                    className={`w-full ${
                      isPopular 
                        ? 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600' 
                        : ''
                    }`}
                    variant={hasThisPlan ? 'secondary' : isPopular ? 'default' : 'outline'}
                    disabled={hasThisPlan || createSubscriptionMutation.isPending || updateSubscriptionMutation.isPending}
                    onClick={handlePlanClick}
                  >
                    {createSubscriptionMutation.isPending || updateSubscriptionMutation.isPending ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      getButtonText()
                    )}
                  </Button>
                </CardFooter>
              </Card>
            );
          })}
        </div>

        {/* Retail Option or Current Subscription Info */}
        <div className="max-w-4xl mx-auto">
          <Card className="bg-white/50 backdrop-blur-sm border-dashed border-2 border-slate-300">
            <CardHeader className="text-center">
              <CardTitle className="text-2xl">
                {userSubscription ? "Your Current Subscription Benefits" : "No Subscription? No Problem!"}
              </CardTitle>
              <CardDescription>
                {userSubscription 
                  ? `You're getting ${Math.round(((getRetailPrice() - userSubscription.pointsPerMonth / 100 * 2) / getRetailPrice()) * 100)}% off retail pricing with your ${userSubscription.planName} plan`
                  : "Purchase points at retail price whenever you need them"
                }
              </CardDescription>
            </CardHeader>
            <CardContent className="text-center">
              <div className="flex justify-center items-baseline mb-4">
                <span className="text-4xl font-bold text-slate-900">
                  ${formatPrice(retailPricePerPage)}
                </span>
                <span className="text-slate-600 ml-2">/page</span>
              </div>
              <p className="text-slate-600 mb-6">
                No monthly commitment • Pay as you go • ${formatPrice(2)} per 100 points
              </p>
              <Button variant="outline" className="w-full max-w-md">
                Buy Points Now
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Cancel Subscription Section for Subscribed Users */}
        {userSubscription && (
          <div className="mt-16 max-w-2xl mx-auto">
            <Card className="border-red-200 bg-red-50/50">
              <CardHeader>
                <CardTitle className="text-red-800">Cancel Subscription</CardTitle>
                <CardDescription className="text-red-600">
                  Your subscription will remain active until {userSubscription?.currentPeriodEnd ? new Date(userSubscription.currentPeriodEnd).toLocaleDateString() : 'your next billing date'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button 
                  variant="destructive" 
                  className="w-full"
                  disabled={cancelSubscriptionMutation.isPending}
                  onClick={() => setCancelConfirmationOpen(true)}
                >
                  {cancelSubscriptionMutation.isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Cancelling...
                    </>
                  ) : (
                    "Cancel Subscription"
                  )}
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {/* FAQ Section */}
        <div className="mt-20 text-center">
          <h2 className="text-3xl font-bold text-slate-900 mb-8">Frequently Asked Questions</h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="text-left">
              <h3 className="text-lg font-semibold text-slate-900 mb-2">How do points work?</h3>
              <p className="text-slate-600">
                Each page of your screenplay costs 100 points to render. Subscriptions include monthly point allowances at discounted rates.
              </p>
            </div>
            <div className="text-left">
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Can I change plans anytime?</h3>
              <p className="text-slate-600">
                Yes! You can upgrade, downgrade, or cancel your subscription at any time. Changes take effect at your next billing cycle.
              </p>
            </div>
            <div className="text-left">
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Do unused points roll over?</h3>
              <p className="text-slate-600">
                Subscription points reset each month, but any additional points you purchase separately never expire.
              </p>
            </div>
            <div className="text-left">
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Is there a free trial?</h3>
              <p className="text-slate-600">
                New users get 500 free points (5 pages) to try our platform. No credit card required for the trial.
              </p>
            </div>
          </div>
        </div>

        {/* Subscription Change Confirmation Dialog */}
        <Dialog open={confirmationDialog.isOpen} onOpenChange={handleConfirmationCancel}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>
                {confirmationDialog.type === 'upgrade' ? 'Confirm Upgrade' : 'Confirm Downgrade'}
              </DialogTitle>
              <DialogDescription className="text-base leading-relaxed">
                {confirmationDialog.type === 'upgrade' ? (
                  <>
                    You will be charged the difference between plans right now on your plan's current card. The full price for the new plan will be charged at the beginning of the next pay period.
                  </>
                ) : (
                  <>
                    You will keep your current plan through the end of the current billing period and then the downgrade will take effect. This will override any other downgrades you have executed prior to this.
                  </>
                )}
              </DialogDescription>
            </DialogHeader>
            
            <div className="flex items-center space-x-2 my-4">
              <Checkbox 
                id="confirmation-checkbox" 
                checked={confirmationChecked}
                onCheckedChange={(checked) => setConfirmationChecked(checked === true)}
              />
              <label 
                htmlFor="confirmation-checkbox" 
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                {confirmationDialog.type === 'upgrade' ? (
                  <>I understand, charge me ${formatPrice(confirmationDialog.priceDifference || 0)} now</>
                ) : (
                  <>I understand. Downgrade me at the start of the next billing period.</>
                )}
              </label>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={handleConfirmationCancel}>
                Cancel
              </Button>
              <Button 
                onClick={handleConfirmationProceed}
                disabled={!confirmationChecked || updateSubscriptionMutation.isPending}
              >
                {updateSubscriptionMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  `Proceed with ${confirmationDialog.type}`
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Cancellation Confirmation Dialog */}
        <Dialog open={cancelConfirmationOpen} onOpenChange={setCancelConfirmationOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Cancel Subscription</DialogTitle>
              <DialogDescription>
                Canceling your subscription means immediately forfeiting your discount on future points. You will keep all points you have purchased up until now.
              </DialogDescription>
            </DialogHeader>
            
            <div className="flex items-center space-x-2 my-4">
              <Checkbox 
                id="cancel-confirmation-checkbox" 
                checked={cancelConfirmationChecked}
                onCheckedChange={(checked) => setCancelConfirmationChecked(checked === true)}
              />
              <label 
                htmlFor="cancel-confirmation-checkbox" 
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                I understand.
              </label>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={handleCancelConfirmationCancel}>
                Keep Subscription
              </Button>
              <Button 
                variant="destructive"
                onClick={handleCancelConfirmationProceed}
                disabled={!cancelConfirmationChecked || cancelSubscriptionMutation.isPending}
              >
                {cancelSubscriptionMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Cancelling...
                  </>
                ) : (
                  "Cancel Subscription"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Stripe Checkout Modal */}
        {checkoutData && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
              <h3 className="text-xl font-semibold mb-4">Complete Your Subscription</h3>
              <Elements 
                stripe={stripePromise} 
                options={{ 
                  clientSecret: checkoutData.clientSecret,
                  appearance: {
                    theme: 'stripe',
                  },
                }}
              >
                <SubscriptionCheckoutForm
                  priceId={checkoutData.priceId}
                  planName={checkoutData.planName}
                  onSuccess={() => {
                    setCheckoutData(null);
                    queryClient.invalidateQueries({ queryKey: ["/api/user/subscription"] });
                  }}
                  onCancel={() => setCheckoutData(null)}
                />
              </Elements>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}